import { apiClient } from "./client";
import type { CanonicalLineageQueryFilters } from "./canonicalLineage";
import { compareInstantsAtMicrosecondPrecision } from "../lib/temporalInstant";

export const LINEAGE_DSM_CONTRACT_VERSION = "thread.lineage.dsm.v1" as const;
export const LINEAGE_DSM_RESOURCE_LIMITS_VERSION = "thread.lineage-dsm-resource-limits.v1" as const;
export const LINEAGE_DSM_PATH = "/thread/v1/provenance/lineage/dsm" as const;
export const LINEAGE_DSM_SCHEMA_MIGRATION = "20260811200000" as const;
export const LINEAGE_DSM_MAX_BANDS = 64;
export const LINEAGE_DSM_MAX_CELLS = 1_000;
export const LINEAGE_DSM_MAX_PAGE_SIZE = 100;
export const LINEAGE_DSM_MAX_DEPTH = 8;
export const LINEAGE_DSM_TOKEN_MAX_LENGTH = 2_048;

export type LineageDsmMatrixKind = "dependency" | "model_context";
export type LineageDsmAggregationBoundary = "artifact_version_binding" | "artifact_instance_relation";
export type LineageDsmRelation = "binding_dependency" | "instance_of";
export type LineageDsmEntityKind = "cluster" | "artifact" | "artifact_version";
export type LineageDsmClusterKind = "semantic" | "remainder" | "partition";
export type LineageDsmDrillKind = "clusters" | "leaves";
export type LineageDsmLevelKind = "clusters" | "leaves";
export type LineageDsmWarning = "lineage_dsm_page_truncated" | "temporal_history_incomplete";

export interface LineageDsmArtifactVersionRef {
  entity_kind: "artifact_version";
  id: string;
}

export interface LineageDsmEntityRef {
  entity_kind: "artifact" | "artifact_version";
  id: string;
}

export interface LineageDsmRequest {
  project_id: string;
  matrix_kind?: LineageDsmMatrixKind;
  query?: string;
  filters?: CanonicalLineageQueryFilters;
  drill_token?: string;
  cursor?: string;
  focus_ref?: LineageDsmArtifactVersionRef;
  page_size?: number;
  as_of?: string | null;
  recorded_as_of?: string;
  snapshot_token?: string;
}

export interface LineageDsmAppliedFilters {
  artifact_kinds: string[];
  lifecycle_states: string[];
  model_classifications: Array<"generic" | "instance" | "unclassified">;
  name_contains: string | null;
  created_after: string | null;
  created_before: string | null;
}

export interface LineageDsmAppliedSelection {
  query: string | null;
  filters: LineageDsmAppliedFilters;
  focus_ref: LineageDsmArtifactVersionRef | null;
  page_size: number;
}

export interface LineageDsmBreadcrumb {
  level: number;
  band_id: string;
  label: string;
}

export interface LineageDsmBand {
  id: string;
  label: string;
  member_count: number;
  entity_kind: LineageDsmEntityKind;
  cluster_kind: LineageDsmClusterKind | null;
  drill_kind: LineageDsmDrillKind | null;
  entity_ref: LineageDsmEntityRef | null;
  selection_ref: LineageDsmArtifactVersionRef | null;
  drill_token: string | null;
}

export interface LineageDsmCell {
  source_band_id: string;
  target_band_id: string;
  relation: LineageDsmRelation;
  count: number;
  heat: number;
}

export interface LineageDsmResult {
  contract_version: typeof LINEAGE_DSM_CONTRACT_VERSION;
  resource_limits_version: typeof LINEAGE_DSM_RESOURCE_LIMITS_VERSION;
  project_id: string;
  matrix_kind: LineageDsmMatrixKind;
  aggregation_boundary: LineageDsmAggregationBoundary;
  applied_selection: LineageDsmAppliedSelection;
  effective_as_of: string;
  recorded_as_of: string;
  snapshot_token: string;
  level: number;
  level_kind: LineageDsmLevelKind;
  breadcrumbs: LineageDsmBreadcrumb[];
  bands: LineageDsmBand[];
  cells: LineageDsmCell[];
  focus_band_id: string | null;
  next_cursor: string | null;
  truncated: boolean;
  warnings: LineageDsmWarning[];
}

export interface LineageDsmEnvelope {
  schema_version: "thread.api.v1";
  request_id: string;
  data: LineageDsmResult;
  page: { next_cursor: string | null; truncated: boolean };
  provenance: {
    source: "thread_core";
    projection_version: typeof LINEAGE_DSM_CONTRACT_VERSION;
    schema_migration: string;
  };
  warnings: string[];
}

export class LineageDsmValidationError extends Error {
  readonly path: string;

  constructor(path: string, expectation: string) {
    super(`${path}: ${expectation}`);
    this.name = "LineageDsmValidationError";
    this.path = path;
  }
}

type UnknownRecord = Record<string, unknown>;

const DOCUMENT_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const REQUEST_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const FIXED_UTC_TIMESTAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{6}Z$/;
const AWARE_TIMESTAMP = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?(Z|(([+-])(\d{2}):(\d{2})))$/;
const SNAPSHOT_TOKEN = /^ts1\.[0-9a-f]{16}\.[A-Za-z0-9_-]{39,4060}$/;
const PYTHON_STRIP_WHITESPACE = new Set([
  ...Array.from({ length: 5 }, (_, index) => String.fromCodePoint(0x09 + index)),
  ...Array.from({ length: 5 }, (_, index) => String.fromCodePoint(0x1c + index)),
  "\u0085", "\u00a0", "\u1680",
  ...Array.from({ length: 11 }, (_, index) => String.fromCodePoint(0x2000 + index)),
  "\u2028", "\u2029", "\u202f", "\u205f", "\u3000",
]);

/** Match Python str.strip(), which intentionally differs from JavaScript trim(). */
function pythonStrip(value: string): string {
  const characters = [...value];
  let start = 0;
  let end = characters.length;
  while (start < end && PYTHON_STRIP_WHITESPACE.has(characters[start]!)) start += 1;
  while (end > start && PYTHON_STRIP_WHITESPACE.has(characters[end - 1]!)) end -= 1;
  return characters.slice(start, end).join("");
}

function fail(path: string, expectation: string): never {
  throw new LineageDsmValidationError(path, expectation);
}

function record(value: unknown, path: string): UnknownRecord {
  if (value === null || typeof value !== "object" || Array.isArray(value)) fail(path, "expected an object");
  return value as UnknownRecord;
}

function exactRecord(value: unknown, path: string, required: readonly string[], optional: readonly string[] = []): UnknownRecord {
  const item = record(value, path);
  for (const key of required) {
    if (!Object.prototype.hasOwnProperty.call(item, key)) fail(`${path}.${key}`, "missing required field");
  }
  const allowed = new Set([...required, ...optional]);
  for (const key in item) {
    if (Object.prototype.hasOwnProperty.call(item, key) && !allowed.has(key)) {
      fail(`${path}.${key}`, "unknown field on a known contract object");
    }
  }
  return item;
}

function string(value: unknown, path: string, min = 1, max = Number.MAX_SAFE_INTEGER): string {
  if (typeof value !== "string") fail(path, "expected a string");
  const length = [...value].length;
  if (length < min || length > max) fail(path, `expected between ${min} and ${max} characters`);
  return value;
}

function safeText(value: unknown, path: string, min = 1, max = Number.MAX_SAFE_INTEGER): string {
  const result = string(value, path, min, max);
  if ([...result].some((character) => character.charCodeAt(0) < 0x20 || character.charCodeAt(0) === 0x7f)) {
    fail(path, "must not contain control characters");
  }
  return result;
}

function nullableString(value: unknown, path: string, min = 0, max = Number.MAX_SAFE_INTEGER): string | null {
  return value === null ? null : string(value, path, min, max);
}

function array(value: unknown, path: string, max: number): unknown[] {
  if (!Array.isArray(value)) fail(path, "expected an array");
  if (value.length > max) fail(path, `must contain at most ${max} items`);
  return value;
}

function integer(value: unknown, path: string, min: number, max = Number.MAX_SAFE_INTEGER): number {
  if (typeof value !== "number" || !Number.isInteger(value) || value < min || value > max) {
    fail(path, `expected an integer between ${min} and ${max}`);
  }
  return value;
}

function bool(value: unknown, path: string): boolean {
  if (typeof value !== "boolean") fail(path, "expected a boolean");
  return value;
}

function literal<T extends string>(value: unknown, path: string, values: readonly T[]): T {
  if (typeof value !== "string" || !values.includes(value as T)) fail(path, `expected one of: ${values.join(", ")}`);
  return value as T;
}

function documentUuid(value: unknown, path: string): string {
  const result = string(value, path);
  if (!DOCUMENT_UUID.test(result)) fail(path, "expected a lowercase canonical UUID");
  return result;
}

function requestUuid(value: unknown, path: string): string {
  const result = string(value, path);
  if (!REQUEST_UUID.test(result)) fail(path, "expected a canonically hyphenated UUID");
  return result;
}

function validTimestamp(value: string): boolean {
  const match = AWARE_TIMESTAMP.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = Number(match[6]);
  const offsetHour = match[8] === "Z" ? 0 : Number(match[11]);
  const offsetMinute = match[8] === "Z" ? 0 : Number(match[12]);
  const boundaryYearUsesUtc = (year !== 1 && year !== 9999) || match[8] === "Z";
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const days = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  const milliseconds = Date.parse(value);
  return year >= 1 && year <= 9999
    && month >= 1 && month <= 12
    && day >= 1 && day <= days[month - 1]!
    && hour <= 23 && minute <= 59 && second <= 59
    && offsetHour <= 23 && offsetMinute <= 59
    && boundaryYearUsesUtc
    && Number.isFinite(milliseconds)
    && new Date(milliseconds).getUTCFullYear() >= 1
    && new Date(milliseconds).getUTCFullYear() <= 9999;
}

function awareTimestamp(value: unknown, path: string): string {
  const result = string(value, path);
  if (!AWARE_TIMESTAMP.test(result) || !validTimestamp(result)) fail(path, "expected an ISO-8601 timestamp with a timezone offset");
  return result;
}

function fixedTimestamp(value: unknown, path: string): string {
  const result = string(value, path);
  if (!FIXED_UTC_TIMESTAMP.test(result) || !validTimestamp(result)) fail(path, "expected UTC with exactly six fractional digits");
  return result;
}

function snapshotToken(value: unknown, path: string): string {
  const result = string(value, path);
  if (!SNAPSHOT_TOKEN.test(result)) fail(path, "expected a canonical ts1 snapshot token");
  return result;
}

function opaqueToken(value: unknown, path: string): string {
  return safeText(value, path, 20, LINEAGE_DSM_TOKEN_MAX_LENGTH);
}

function parseArtifactVersionRef(value: unknown, path: string, request = false): LineageDsmArtifactVersionRef {
  const item = exactRecord(value, path, ["entity_kind", "id"]);
  literal(item.entity_kind, `${path}.entity_kind`, ["artifact_version"]);
  if (request) requestUuid(item.id, `${path}.id`); else documentUuid(item.id, `${path}.id`);
  return value as LineageDsmArtifactVersionRef;
}

function parseEntityRef(value: unknown, path: string): LineageDsmEntityRef {
  const item = exactRecord(value, path, ["entity_kind", "id"]);
  literal(item.entity_kind, `${path}.entity_kind`, ["artifact", "artifact_version"]);
  documentUuid(item.id, `${path}.id`);
  return value as LineageDsmEntityRef;
}

function parseFilters(value: unknown, path: string): LineageDsmAppliedFilters {
  const item = exactRecord(value, path, [
    "artifact_kinds", "lifecycle_states", "model_classifications", "name_contains", "created_after", "created_before",
  ]);
  const artifactKinds = array(item.artifact_kinds, `${path}.artifact_kinds`, 50).map((entry, index) => (
    safeText(entry, `${path}.artifact_kinds[${index}]`, 1, 100)
  ));
  const lifecycleStates = array(item.lifecycle_states, `${path}.lifecycle_states`, 20).map((entry, index) => (
    safeText(entry, `${path}.lifecycle_states[${index}]`, 1, 100)
  ));
  const classifications = array(item.model_classifications, `${path}.model_classifications`, 3).map((entry, index) => (
    literal(entry, `${path}.model_classifications[${index}]`, ["generic", "instance", "unclassified"])
  ));
  for (const [values, key] of [[artifactKinds, "artifact_kinds"], [lifecycleStates, "lifecycle_states"], [classifications, "model_classifications"]] as const) {
    if (values.join("\u0000") !== [...new Set(values)].sort(compareUnicodeCodePoints).join("\u0000")) {
      fail(`${path}.${key}`, "must be unique and canonically sorted");
    }
    if (values.some((entry) => pythonStrip(entry) !== entry)) {
      fail(`${path}.${key}`, "must use Python-trimmed canonical spelling");
    }
  }
  if (item.name_contains !== null) {
    const name = safeText(item.name_contains, `${path}.name_contains`, 1, 300);
    if (pythonStrip(name) !== name) fail(`${path}.name_contains`, "must use trimmed canonical spelling");
  }
  if (item.created_after !== null) fixedTimestamp(item.created_after, `${path}.created_after`);
  if (item.created_before !== null) fixedTimestamp(item.created_before, `${path}.created_before`);
  if (item.created_after !== null && item.created_before !== null
    && compareInstantsAtMicrosecondPrecision(item.created_after as string, item.created_before as string) > 0) {
    fail(`${path}.created_after`, "must not be later than created_before");
  }
  return value as LineageDsmAppliedFilters;
}

function parseBand(value: unknown, path: string): LineageDsmBand {
  const item = exactRecord(value, path, [
    "id", "label", "member_count", "entity_kind", "cluster_kind", "drill_kind", "entity_ref", "selection_ref", "drill_token",
  ]);
  safeText(item.id, `${path}.id`, 1, 200);
  safeText(item.label, `${path}.label`, 1, 300);
  const memberCount = integer(item.member_count, `${path}.member_count`, 1);
  const entityKind = literal(item.entity_kind, `${path}.entity_kind`, ["cluster", "artifact", "artifact_version"]);
  if (entityKind === "cluster") {
    literal(item.cluster_kind, `${path}.cluster_kind`, ["semantic", "remainder", "partition"]);
    literal(item.drill_kind, `${path}.drill_kind`, ["clusters", "leaves"]);
    if (item.entity_ref !== null) fail(`${path}.entity_ref`, "cluster bands cannot expose an entity_ref");
    opaqueToken(item.drill_token, `${path}.drill_token`);
    if (item.selection_ref !== null) fail(`${path}.selection_ref`, "cluster bands cannot expose a selection_ref");
  } else {
    if (item.cluster_kind !== null || item.drill_kind !== null || item.drill_token !== null) {
      fail(path, "leaf bands cannot expose cluster drill metadata");
    }
    if (memberCount !== 1) fail(`${path}.member_count`, "leaf bands represent exactly one member");
    const entityRef = parseEntityRef(item.entity_ref, `${path}.entity_ref`);
    const selectionRef = parseArtifactVersionRef(item.selection_ref, `${path}.selection_ref`);
    if (entityRef.entity_kind !== entityKind) fail(`${path}.entity_ref.entity_kind`, "must match the leaf entity_kind");
    if (entityKind === "artifact_version" && entityRef.id !== selectionRef.id) {
      fail(`${path}.selection_ref.id`, "ArtifactVersion leaves must select their own identity");
    }
  }
  return value as LineageDsmBand;
}

function parseResult(value: unknown, path: string): LineageDsmResult {
  const item = exactRecord(value, path, [
    "contract_version", "resource_limits_version", "project_id", "matrix_kind", "aggregation_boundary", "applied_selection",
    "effective_as_of", "recorded_as_of", "snapshot_token", "level", "level_kind", "breadcrumbs", "bands", "cells", "focus_band_id",
    "next_cursor", "truncated", "warnings",
  ]);
  literal(item.contract_version, `${path}.contract_version`, [LINEAGE_DSM_CONTRACT_VERSION]);
  literal(item.resource_limits_version, `${path}.resource_limits_version`, [LINEAGE_DSM_RESOURCE_LIMITS_VERSION]);
  documentUuid(item.project_id, `${path}.project_id`);
  const matrixKind = literal(item.matrix_kind, `${path}.matrix_kind`, ["dependency", "model_context"]);
  const boundary = literal(item.aggregation_boundary, `${path}.aggregation_boundary`, ["artifact_version_binding", "artifact_instance_relation"]);
  const expectedBoundary: LineageDsmAggregationBoundary = matrixKind === "dependency"
    ? "artifact_version_binding" : "artifact_instance_relation";
  if (boundary !== expectedBoundary) fail(`${path}.aggregation_boundary`, "must match matrix_kind");
  const applied = exactRecord(item.applied_selection, `${path}.applied_selection`, ["query", "filters", "focus_ref", "page_size"]);
  if (applied.query !== null) {
    const query = safeText(applied.query, `${path}.applied_selection.query`, 1, 300);
    if (pythonStrip(query) !== query) fail(`${path}.applied_selection.query`, "must be trimmed");
  }
  parseFilters(applied.filters, `${path}.applied_selection.filters`);
  const appliedFocus = applied.focus_ref === null
    ? null
    : parseArtifactVersionRef(applied.focus_ref, `${path}.applied_selection.focus_ref`);
  const appliedPageSize = integer(applied.page_size, `${path}.applied_selection.page_size`, 1, LINEAGE_DSM_MAX_PAGE_SIZE);
  const effective = fixedTimestamp(item.effective_as_of, `${path}.effective_as_of`);
  const recorded = fixedTimestamp(item.recorded_as_of, `${path}.recorded_as_of`);
  if (compareInstantsAtMicrosecondPrecision(effective, recorded) > 0) {
    fail(`${path}.effective_as_of`, "must not be later than recorded_as_of");
  }
  snapshotToken(item.snapshot_token, `${path}.snapshot_token`);
  const level = integer(item.level, `${path}.level`, 0, LINEAGE_DSM_MAX_DEPTH);
  const levelKind = literal(item.level_kind, `${path}.level_kind`, ["clusters", "leaves"]);
  const breadcrumbs = array(item.breadcrumbs, `${path}.breadcrumbs`, LINEAGE_DSM_MAX_DEPTH);
  breadcrumbs.forEach((entry, index) => {
    const crumbPath = `${path}.breadcrumbs[${index}]`;
    const crumb = exactRecord(entry, crumbPath, ["level", "band_id", "label"]);
    integer(crumb.level, `${crumbPath}.level`, 0, LINEAGE_DSM_MAX_DEPTH - 1);
    safeText(crumb.band_id, `${crumbPath}.band_id`, 1, 200);
    const breadcrumbLabel = safeText(crumb.label, `${crumbPath}.label`, 1, 16);
    if (!/^[\x20-\x7e]+$/u.test(breadcrumbLabel)) {
      fail(`${crumbPath}.label`, "must contain only printable ASCII");
    }
  });
  const bands = array(item.bands, `${path}.bands`, LINEAGE_DSM_MAX_PAGE_SIZE).map((entry, index) => (
    parseBand(entry, `${path}.bands[${index}]`)
  ));
  const ids = bands.map((band) => band.id);
  if (new Set(ids).size !== ids.length) fail(`${path}.bands`, "band ids must be unique");
  const knownIds = new Set(ids);
  const expectedRelation: LineageDsmRelation = matrixKind === "dependency" ? "binding_dependency" : "instance_of";
  const cells = array(item.cells, `${path}.cells`, LINEAGE_DSM_MAX_CELLS).map((entry, index) => {
    const cellPath = `${path}.cells[${index}]`;
    const cell = exactRecord(entry, cellPath, ["source_band_id", "target_band_id", "relation", "count", "heat"]);
    const sourceId = safeText(cell.source_band_id, `${cellPath}.source_band_id`, 1, 200);
    const targetId = safeText(cell.target_band_id, `${cellPath}.target_band_id`, 1, 200);
    if (!knownIds.has(sourceId) || !knownIds.has(targetId)) fail(cellPath, "must reference returned bands");
    if (literal(cell.relation, `${cellPath}.relation`, ["binding_dependency", "instance_of"]) !== expectedRelation) {
      fail(`${cellPath}.relation`, "must match matrix_kind");
    }
    integer(cell.count, `${cellPath}.count`, 1);
    integer(cell.heat, `${cellPath}.heat`, 1, 1_000);
    return entry as LineageDsmCell;
  });
  const cellKeys = cells.map((cell) => `${cell.source_band_id}\u0000${cell.target_band_id}\u0000${cell.relation}`);
  if (new Set(cellKeys).size !== cellKeys.length) fail(`${path}.cells`, "sparse cell identities must be unique");
  const bandOrder = new Map(ids.map((id, index) => [id, index]));
  const orderedCellKeys = cells.map((cell) => [
    bandOrder.get(cell.source_band_id)!,
    bandOrder.get(cell.target_band_id)!,
    cell.relation,
  ] as const);
  const sortedCellKeys = [...orderedCellKeys].sort((left, right) => (
    left[0] - right[0] || left[1] - right[1] || left[2].localeCompare(right[2])
  ));
  if (orderedCellKeys.some((key, index) => key[0] !== sortedCellKeys[index]![0]
    || key[1] !== sortedCellKeys[index]![1]
    || key[2] !== sortedCellKeys[index]![2])) {
    fail(`${path}.cells`, "must follow returned source/target band order");
  }
  const maximumCount = cells.reduce((maximum, cell) => Math.max(maximum, cell.count), 0);
  cells.forEach((cell, index) => {
    const expectedHeat = Number((BigInt(cell.count) * 1_000n + BigInt(maximumCount) - 1n) / BigInt(maximumCount));
    if (cell.heat !== expectedHeat) fail(`${path}.cells[${index}].heat`, "must be ceiling-normalized against this response");
  });
  const focusBandId = nullableString(item.focus_band_id, `${path}.focus_band_id`, 1, 200);
  if (focusBandId !== null && !knownIds.has(focusBandId)) fail(`${path}.focus_band_id`, "must reference a returned band");
  const nextCursor = item.next_cursor === null ? null : opaqueToken(item.next_cursor, `${path}.next_cursor`);
  const truncated = bool(item.truncated, `${path}.truncated`);
  const warnings = array(item.warnings, `${path}.warnings`, 2).map((entry, index) => (
    literal(entry, `${path}.warnings[${index}]`, ["lineage_dsm_page_truncated", "temporal_history_incomplete"])
  ));
  const incomplete = warnings.includes("temporal_history_incomplete");
  if (incomplete) {
    if (warnings.length !== 1 || level !== 0 || levelKind !== "clusters" || breadcrumbs.length || bands.length || cells.length || focusBandId !== null || nextCursor !== null || !truncated) {
      fail(path, "incomplete history must use the fixed empty truncated shape");
    }
    return value as LineageDsmResult;
  }
  if (level === 0) {
    if (levelKind !== "clusters") fail(`${path}.level_kind`, "root matrices are cluster levels");
    if (breadcrumbs.length) fail(`${path}.breadcrumbs`, "root matrices cannot expose breadcrumbs");
    if (bands.length > LINEAGE_DSM_MAX_BANDS || bands.some((band) => band.entity_kind !== "cluster")) {
      fail(`${path}.bands`, "root matrices contain at most 64 cluster bands");
    }
    if (bands.some((band) => band.cluster_kind !== "semantic" && band.cluster_kind !== "remainder")) {
      fail(`${path}.bands`, "root matrices contain only kind and remainder bands");
    }
    if (bands.some((band) => band.drill_kind !== "clusters")) {
      fail(`${path}.bands`, "every root band must advance to one cluster level");
    }
    const remainderPositions = bands.flatMap((band, index) => band.cluster_kind === "remainder" ? [index] : []);
    if (remainderPositions.length > 1
      || (remainderPositions.length === 1 && remainderPositions[0] !== bands.length - 1)) {
      fail(`${path}.bands`, "a root may contain at most one final remainder band");
    }
    if (nextCursor !== null || truncated || warnings.length) fail(path, "root matrices are complete and unpaginated");
    if ((appliedFocus === null) !== (focusBandId === null)) {
      fail(`${path}.focus_band_id`, "complete roots must resolve exactly the applied focus_ref");
    }
  } else {
    if (breadcrumbs.length !== level || breadcrumbs.some((crumb, index) => (crumb as LineageDsmBreadcrumb).level !== index)) {
      fail(`${path}.breadcrumbs`, "must be contiguous from the root through the parent level");
    }
    if (levelKind === "clusters") {
      if (bands.some((band) => band.entity_kind !== "cluster")) fail(`${path}.bands`, "a level cannot mix clusters and leaves");
      if (nextCursor !== null || truncated || warnings.length) fail(path, "cluster levels are complete and unpaginated");
      if (bands.length > LINEAGE_DSM_MAX_BANDS) fail(`${path}.bands`, "cluster levels contain at most 64 bands");
      if (level === LINEAGE_DSM_MAX_DEPTH) fail(`${path}.bands`, "maximum hierarchy depth cannot expose another drill");
      const clusterKinds = bands.map((band) => band.cluster_kind);
      const drillKinds = new Set(bands.map((band) => band.drill_kind));
      const remainderPositions = clusterKinds.flatMap((kind, index) => kind === "remainder" ? [index] : []);
      if (remainderPositions.length) {
        if (remainderPositions.length !== 1
          || remainderPositions[0] !== bands.length - 1
          || clusterKinds.some((kind) => kind !== "semantic" && kind !== "remainder")
          || drillKinds.size !== 1
          || !drillKinds.has("clusters")) {
          fail(`${path}.bands`, "kind-tail levels require semantic bands and one final cluster remainder");
        }
      } else if (clusterKinds.includes("partition")) {
        if (clusterKinds.some((kind) => kind !== "partition")
          || drillKinds.size !== 1
          || !drillKinds.has("clusters")) {
          fail(`${path}.bands`, "partition levels contain only cluster partitions");
        }
      } else if (clusterKinds.length
        && (clusterKinds.some((kind) => kind !== "semantic") || drillKinds.size !== 1)) {
        fail(`${path}.bands`, "semantic cluster levels require one homogeneous child kind");
      }
    } else {
      const expectedLeafKind: LineageDsmEntityKind = matrixKind === "dependency" ? "artifact_version" : "artifact";
      if (bands.some((band) => band.entity_kind !== expectedLeafKind)) {
        fail(`${path}.bands`, `${matrixKind} leaf matrices require ${expectedLeafKind} bands`);
      }
      if (bands.length > appliedPageSize) fail(`${path}.bands`, "leaf bands cannot exceed applied_selection.page_size");
      const hasNext = nextCursor !== null;
      if (truncated !== hasNext) fail(`${path}.truncated`, "leaf truncation must match next_cursor");
      if (hasNext && bands.length !== appliedPageSize) {
        fail(`${path}.bands`, "truncated leaf pages must contain exactly applied_selection.page_size bands");
      }
      const expectedWarnings = hasNext ? ["lineage_dsm_page_truncated"] : [];
      if (warnings.join("\u0000") !== expectedWarnings.join("\u0000")) fail(`${path}.warnings`, "leaf warnings must match continuation state");
    }
  }
  return value as LineageDsmResult;
}

function validateRequest(request: LineageDsmRequest): void {
  const item = exactRecord(request, "request", ["project_id"], [
    "matrix_kind", "query", "filters", "drill_token", "cursor", "focus_ref", "page_size", "as_of", "recorded_as_of", "snapshot_token",
  ]);
  requestUuid(item.project_id, "request.project_id");
  if (item.matrix_kind !== undefined) literal(item.matrix_kind, "request.matrix_kind", ["dependency", "model_context"]);
  if (item.query !== undefined) {
    const rawQuery = safeText(item.query, "request.query", 1, 300);
    const query = pythonStrip(rawQuery);
    if (!query) fail("request.query", "must not be blank");
    safeText(query, "request.query", 1, 300);
  }
  if (item.filters !== undefined) {
    const filters = exactRecord(item.filters, "request.filters", [], [
      "artifact_kinds", "lifecycle_states", "model_classifications", "name_contains", "created_after", "created_before",
    ]);
    const artifactKinds = filters.artifact_kinds === undefined ? [] : array(filters.artifact_kinds, "request.filters.artifact_kinds", 50)
      .map((entry, index) => pythonStrip(safeText(entry, `request.filters.artifact_kinds[${index}]`)))
      .map((entry, index) => safeText(entry, `request.filters.artifact_kinds[${index}]`, 1, 100));
    const lifecycleStates = filters.lifecycle_states === undefined ? [] : array(filters.lifecycle_states, "request.filters.lifecycle_states", 20)
      .map((entry, index) => pythonStrip(safeText(entry, `request.filters.lifecycle_states[${index}]`)))
      .map((entry, index) => safeText(entry, `request.filters.lifecycle_states[${index}]`, 1, 100));
    if (artifactKinds.some((entry) => !entry) || new Set(artifactKinds).size !== artifactKinds.length) {
      fail("request.filters.artifact_kinds", "must be unique after trimming");
    }
    if (lifecycleStates.some((entry) => !entry) || new Set(lifecycleStates).size !== lifecycleStates.length) {
      fail("request.filters.lifecycle_states", "must be unique after trimming");
    }
    const modelClassifications = filters.model_classifications === undefined ? [] : array(filters.model_classifications, "request.filters.model_classifications", 3)
      .map((entry, index) => literal(entry, `request.filters.model_classifications[${index}]`, ["generic", "instance", "unclassified"]));
    if (modelClassifications.join("\u0000") !== [...new Set(modelClassifications)].sort(compareUnicodeCodePoints).join("\u0000")) {
      fail("request.filters.model_classifications", "must be unique and canonically sorted");
    }
    if (filters.name_contains !== undefined) {
      const rawName = safeText(filters.name_contains, "request.filters.name_contains", 1, 300);
      const name = pythonStrip(rawName);
      if (!name) fail("request.filters.name_contains", "must not be blank");
      safeText(name, "request.filters.name_contains", 1, 300);
    }
    if (filters.created_after !== undefined) awareTimestamp(filters.created_after, "request.filters.created_after");
    if (filters.created_before !== undefined) awareTimestamp(filters.created_before, "request.filters.created_before");
    if (typeof filters.created_after === "string" && typeof filters.created_before === "string"
      && compareInstantsAtMicrosecondPrecision(filters.created_after, filters.created_before) > 0) {
      fail("request.filters.created_after", "must not be later than created_before");
    }
  }
  if (item.as_of !== undefined && item.as_of !== null) awareTimestamp(item.as_of, "request.as_of");
  const hasRecorded = item.recorded_as_of !== undefined;
  const hasSnapshot = item.snapshot_token !== undefined;
  if (hasRecorded !== hasSnapshot) fail("request", "recorded_as_of and snapshot_token must be supplied together");
  if (hasRecorded) {
    const recordedAsOf = fixedTimestamp(item.recorded_as_of, "request.recorded_as_of");
    snapshotToken(item.snapshot_token, "request.snapshot_token");
    if (typeof item.as_of !== "string") fail("request.as_of", "is required for an exact replay");
    if (compareInstantsAtMicrosecondPrecision(item.as_of as string, recordedAsOf) > 0) {
      fail("request.as_of", "must not be later than recorded_as_of");
    }
  }
  if (item.drill_token !== undefined) {
    opaqueToken(item.drill_token, "request.drill_token");
    if (!hasRecorded || typeof item.as_of !== "string") fail("request.drill_token", "requires the exact temporal trio");
  }
  if (item.cursor !== undefined) {
    opaqueToken(item.cursor, "request.cursor");
    if (item.drill_token === undefined) fail("request.cursor", "requires drill_token");
  }
  if (item.focus_ref !== undefined) {
    parseArtifactVersionRef(item.focus_ref, "request.focus_ref", true);
    if (item.drill_token !== undefined || item.cursor !== undefined) fail("request.focus_ref", "cannot be combined with drill or cursor");
  }
  if (item.page_size !== undefined) integer(item.page_size, "request.page_size", 1, LINEAGE_DSM_MAX_PAGE_SIZE);
}

function sortedTrimmed(values: readonly string[] | undefined): string[] {
  return [...new Set((values ?? []).map(pythonStrip))].sort(compareUnicodeCodePoints);
}

function compareUnicodeCodePoints(left: string, right: string): number {
  const leftPoints = [...left].map((character) => character.codePointAt(0)!);
  const rightPoints = [...right].map((character) => character.codePointAt(0)!);
  const length = Math.min(leftPoints.length, rightPoints.length);
  for (let index = 0; index < length; index += 1) {
    if (leftPoints[index] !== rightPoints[index]) return leftPoints[index]! < rightPoints[index]! ? -1 : 1;
  }
  return leftPoints.length - rightPoints.length;
}

function assertRequestBinding(result: LineageDsmResult, request: LineageDsmRequest): void {
  if (result.project_id !== request.project_id.toLowerCase()) fail("$.data.project_id", "must equal request.project_id");
  if (result.matrix_kind !== (request.matrix_kind ?? "dependency")) fail("$.data.matrix_kind", "must equal request.matrix_kind");
  if (typeof request.as_of === "string"
    && compareInstantsAtMicrosecondPrecision(result.effective_as_of, request.as_of) !== 0) {
    fail("$.data.effective_as_of", "must equal the requested as_of instant");
  }
  if (request.recorded_as_of !== undefined && result.recorded_as_of !== request.recorded_as_of) {
    fail("$.data.recorded_as_of", "must equal request.recorded_as_of");
  }
  if (request.snapshot_token !== undefined && result.snapshot_token !== request.snapshot_token) {
    fail("$.data.snapshot_token", "must equal request.snapshot_token");
  }
  const expectedQuery = request.query === undefined ? null : pythonStrip(request.query) || null;
  if (result.applied_selection.query !== expectedQuery) fail("$.data.applied_selection.query", "must equal the normalized request query");
  const requestFilters = request.filters ?? {};
  const expectedClassifications = sortedTrimmed(requestFilters.model_classifications);
  if (result.applied_selection.filters.artifact_kinds.join("\u0000") !== sortedTrimmed(requestFilters.artifact_kinds).join("\u0000")
    || result.applied_selection.filters.lifecycle_states.join("\u0000") !== sortedTrimmed(requestFilters.lifecycle_states).join("\u0000")
    || result.applied_selection.filters.model_classifications.join("\u0000") !== expectedClassifications.join("\u0000")) {
    fail("$.data.applied_selection.filters", "must equal the canonically sorted request filters");
  }
  if (result.applied_selection.filters.name_contains !== (
    requestFilters.name_contains === undefined ? null : pythonStrip(requestFilters.name_contains) || null
  )) {
    fail("$.data.applied_selection.filters.name_contains", "must equal the normalized request filter");
  }
  for (const key of ["created_after", "created_before"] as const) {
    const requested = requestFilters[key];
    const applied = result.applied_selection.filters[key];
    if ((requested == null) !== (applied === null)
      || (requested != null && applied !== null && compareInstantsAtMicrosecondPrecision(requested, applied) !== 0)) {
      fail(`$.data.applied_selection.filters.${key}`, "must equal the requested instant");
    }
  }
  if (result.applied_selection.page_size !== (request.page_size ?? 50)) {
    fail("$.data.applied_selection.page_size", "must equal request.page_size");
  }
  if (request.drill_token === undefined) {
    const expectedFocusId = request.focus_ref?.id.toLowerCase() ?? null;
    const appliedFocusId = result.applied_selection.focus_ref?.id ?? null;
    if (appliedFocusId !== expectedFocusId) {
      fail("$.data.applied_selection.focus_ref", "must equal the normalized root request focus_ref");
    }
  }
}

export function parseLineageDsmEnvelope(value: unknown, request?: LineageDsmRequest): LineageDsmEnvelope {
  const item = exactRecord(value, "$", ["schema_version", "request_id", "data", "page", "provenance", "warnings"]);
  literal(item.schema_version, "$.schema_version", ["thread.api.v1"]);
  documentUuid(item.request_id, "$.request_id");
  const data = parseResult(item.data, "$.data");
  const page = exactRecord(item.page, "$.page", ["next_cursor", "truncated"]);
  const pageCursor = page.next_cursor === null ? null : opaqueToken(page.next_cursor, "$.page.next_cursor");
  const pageTruncated = bool(page.truncated, "$.page.truncated");
  if (pageCursor !== data.next_cursor || pageTruncated !== data.truncated) fail("$.page", "must mirror data pagination");
  const provenance = exactRecord(item.provenance, "$.provenance", ["source", "projection_version", "schema_migration"]);
  literal(provenance.source, "$.provenance.source", ["thread_core"]);
  literal(provenance.projection_version, "$.provenance.projection_version", [LINEAGE_DSM_CONTRACT_VERSION]);
  literal(provenance.schema_migration, "$.provenance.schema_migration", [LINEAGE_DSM_SCHEMA_MIGRATION]);
  if (array(item.warnings, "$.warnings", 100).length) fail("$.warnings", "DSM semantic warnings belong only to data.warnings");
  if (request) assertRequestBinding(data, request);
  return value as LineageDsmEnvelope;
}

export async function fetchLineageDsm(
  request: LineageDsmRequest,
  options: { signal?: AbortSignal } = {},
): Promise<LineageDsmEnvelope> {
  validateRequest(request);
  const response = await apiClient<unknown>(LINEAGE_DSM_PATH, {
    method: "POST",
    body: request,
    signal: options.signal,
  });
  return parseLineageDsmEnvelope(response, request);
}
